import React, { Fragment, useState } from 'react';
import { ApolloError, useMutation } from '@apollo/client';
import { map, replace } from 'lodash';
import { Box, Text, FormControl, useModalState } from '@bechtel/gds';
import {
  Attachment,
  AttachmentMetadataInput,
  AddWorkPackageAttachmentInput,
  AddWorkPackageAttachmentPayload,
  AddWorkPackageAttachmentError,
  IsoDocument,
  AddCommodityAttachmentInput,
  AddCommodityAttachmentPayload,
  AddCommodityAttachmentError,
} from '@bechtel/types';
import { ADD_WORK_PACKAGE_ATTACHMENTS, ADD_COMMODITY_ATTACHMENTS } from '@bechtel/client-gql';
import { FileUpload, FileUploadInput, FileUploadResponse } from '~/components';
import { useDate, useName } from '~/hooks';
import { AddAttachmentCustomError, DocumentUploadError } from './document-upload-error';
// @ts-ignore: tsc declarations not recognising types/index.d.ts
import { ReactComponent as SearchResultsImage } from '@bechtel/client-assets/dist/images/search-results.svg';
import { DocumentLinkStyled, DocumentListStyled, DocumentListSectionStyled } from './styled';
import { t, Trans } from '@lingui/macro';

export interface DocumentListProps {
  documents?: Attachment[];
  isoDocument?: IsoDocument | null;
  workPackageId?: string | null;
  commodityId?: string | null;
  onSelect: (document: Attachment) => void;
  onIsoSelect?: (isoDocument: IsoDocument) => void;
  selectedDocumentId?: string;
  canUploadDocument: boolean;
}

export const DocumentList: React.FC<DocumentListProps> = ({
  documents,
  isoDocument,
  workPackageId,
  commodityId,
  onSelect,
  onIsoSelect,
  selectedDocumentId,
  canUploadDocument,
}) => {
  const { formatISODate } = useDate();
  const { getFullName } = useName();
  const { visible: errorModalVisible, open: openErrorModal, close: closeErrorModal } = useModalState(false);
  const [addAttachmentError, setAddAttachmentError] = useState<
    AddWorkPackageAttachmentError | ApolloError | null | AddCommodityAttachmentError | AddAttachmentCustomError
  >(null);

  const [addAttachment, { loading: wpAttachmentLoading }] = useMutation<
    { addAttachmentsPayload: AddWorkPackageAttachmentPayload },
    { input: AddWorkPackageAttachmentInput }
  >(ADD_WORK_PACKAGE_ATTACHMENTS, {
    onCompleted: (data) => {
      if (data.addAttachmentsPayload?.error) {
        setAddAttachmentError(data.addAttachmentsPayload.error);
        openErrorModal();
      }
    },
    onError: (error) => {
      setAddAttachmentError(error);
      openErrorModal();
    },
  });

  const [uploadCommodityAttachment, { loading: commodityAttachmentLoading }] = useMutation<
    { addAttachmentsPayload: AddCommodityAttachmentPayload },
    { input: AddCommodityAttachmentInput }
  >(ADD_COMMODITY_ATTACHMENTS, {
    onCompleted: (data) => {
      if (data.addAttachmentsPayload?.error) {
        setAddAttachmentError(data.addAttachmentsPayload.error);
        openErrorModal();
      }
    },
    onError: (error) => {
      setAddAttachmentError(error);
      openErrorModal();
    },
  });

  return (
    <DocumentListStyled flexDirection={{ sm: 'column' }}>
      {errorModalVisible && addAttachmentError && (
        <DocumentUploadError error={addAttachmentError} close={closeErrorModal}></DocumentUploadError>
      )}
      <DocumentListSectionStyled canUploadDocument={canUploadDocument}>
        {isoDocument && (
          <DocumentLinkStyled
            onClick={() => {
              if (!onIsoSelect) return;
              onIsoSelect(isoDocument);
            }}
            isSelected={selectedDocumentId === isoDocument.documentNumber}
            data-testid="iso-link"
          >
            <Box justifyContent={{ sm: 'space-between' }} flexDirection={{ sm: 'column' }}>
              <Box mb={{ sm: '300' }}>
                <Text weight="600" size="400">
                  <Trans>ISO</Trans>
                </Text>
              </Box>
              <Text color="grey.500" size="300">
                {isoDocument.documentNumber}
              </Text>
            </Box>
          </DocumentLinkStyled>
        )}
        {documents && documents?.length > 0 && (
          <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
            {map(documents, (document, index) => {
              return (
                <DocumentLinkStyled
                  onClick={() => onSelect(document)}
                  key={index}
                  isSelected={selectedDocumentId === document.id}
                  data-testid="document-link"
                >
                  <Box justifyContent={{ sm: 'space-between' }} flexDirection={{ sm: 'column' }}>
                    <Box mb={{ sm: '300' }}>
                      <Text weight="600" size="400">
                        {replace(document.fileName, /\.[^/.]+$/, '')}
                      </Text>
                    </Box>
                    <Box mb={{ sm: '300' }}>
                      <Text color="grey.600" size="300">
                        <Trans>Uploaded By</Trans>:
                        {getFullName(document.creator?.firstName, document.creator?.lastName)}
                      </Text>
                    </Box>

                    <Text color="grey.500" size="300">
                      <Trans>Uploaded date</Trans>: {formatISODate(document.createdAt)}
                    </Text>
                  </Box>
                </DocumentLinkStyled>
              );
            })}
          </Box>
        )}
        {(!documents || documents.length === 0) && !isoDocument && (
          <Box
            flexGrow={{ sm: 1 }}
            justifyContent={{ sm: 'center' }}
            flexDirection={{ sm: 'column' }}
            px={{ sm: '600' }}
            py={{ sm: '900' }}
            alignItems={{ sm: 'center' }}
          >
            <Box mb={{ sm: '500' }}>
              <Text color="grey.600" weight="600" family="graphik">
                <Trans>No documents to show</Trans>
              </Text>
            </Box>
            <Box mb={{ sm: '300' }}>
              <Text color="grey.600" family="graphik">
                <Trans>Once documents are added,</Trans>
              </Text>
            </Box>
            <Box mb={{ sm: '900' }}>
              <Text color="grey.600" family="graphik">{t`you'll see them listed here`}</Text>
            </Box>
            <SearchResultsImage></SearchResultsImage>
          </Box>
        )}
      </DocumentListSectionStyled>
      {canUploadDocument && (
        <Box p={{ sm: '500' }}>
          <FormControl label={t`Upload a Document`} style={{ flex: '1' }}>
            <FileUpload
            maxFileSizeMB={10}
              onSuccess={(res: FileUploadResponse, file) => {
                const newMediaData: AttachmentMetadataInput = {
                  fileName: file.name,
                  fileSize: file.size,
                  attachmentId: res.attachmentId,
                  mimeType: file.type,
                };
                if (commodityId) {
                  uploadCommodityAttachment({
                    variables: {
                      input: {
                        commodityId: commodityId,
                        attachmentMetadata: [newMediaData],
                      },
                    },
                  });
                } else {
                  addAttachment({
                    variables: {
                      input: {
                        workPackageId: workPackageId,
                        attachmentMetadata: [newMediaData],
                      },
                    },
                  });
                }
              }}
              maxFiles={100} //On assumption that there may not be more than 100 documents uploaded for a workpackage/commodity
              acceptFileTypes="application/pdf"
            >
              {({ getInputProps, getRootProps, loading, acceptFileTypes }) => {
                return (
                  <Fragment>
                    <FileUploadInput
                      getInputProps={getInputProps}
                      getRootProps={getRootProps}
                      loading={loading || wpAttachmentLoading || commodityAttachmentLoading}
                      acceptFileTypes={acceptFileTypes}
                    />
                  </Fragment>
                );
              }}
            </FileUpload>
          </FormControl>
        </Box>
      )}
    </DocumentListStyled>
  );
};
